import { useState, type FormEvent } from 'react';
import type { ContactListItem } from '@neuroedge/shared';
import { Button, TextField, useToast } from '../../components/ui';
import { listContacts, useCreateContact } from '../../api/contacts';
import { fieldErrorsFromApiError } from '../../lib/field-errors';

const digits = (s: string) => s.replace(/\D/g, '');

/** Same number if the trailing 10 digits match ("+91 98765 43210" vs "9876543210"). */
function samePhone(a: string | null, b: string): boolean {
  if (!a) return false;
  const x = digits(a);
  const y = digits(b);
  return x.length >= 7 && y.length >= 7 && x.slice(-10) === y.slice(-10);
}

/** Mirrors the API's `createContactBody` limits; a phone is required to send over WhatsApp. */
function validate(name: string, phone: string): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!name.trim()) errors.displayName = 'Enter the customer’s name.';
  else if (name.trim().length > 200) errors.displayName = 'Must be 200 characters or fewer.';
  const d = digits(phone);
  if (!phone.trim()) errors.phone = 'Enter a mobile number - the quotation is sent on WhatsApp.';
  else if (phone.trim().length > 40 || d.length < 7 || d.length > 15) {
    errors.phone = 'Enter a valid number, e.g. +91 98765 43210.';
  } else if (!/^[+\d\s()-]+$/.test(phone.trim())) {
    errors.phone = 'Use digits, spaces, +, - or ( ) only.';
  }
  return errors;
}

/**
 * Inline "new customer" on the New Quotation screen: name + mobile, saved
 * as a regular contact (so it shows in Contacts). If a contact with the
 * same number already exists it is picked instead of creating a duplicate.
 */
export function QuickAddContact({
  initialText,
  onAdded,
  onCancel,
}: {
  initialText: string;
  onAdded: (contact: ContactListItem) => void;
  onCancel: () => void;
}) {
  const toast = useToast();
  const create = useCreateContact();
  const looksLikePhone = /^[+\d\s()-]{5,}$/.test(initialText.trim());
  const [name, setName] = useState(looksLikePhone ? '' : initialText.trim());
  const [phone, setPhone] = useState(looksLikePhone ? initialText.trim() : '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [checking, setChecking] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    const found = validate(name, phone);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setChecking(true);
    try {
      const existing = await listContacts({ search: digits(phone).slice(-10), pageSize: 10 });
      const match = existing.items.find((c) => samePhone(c.phone, phone));
      if (match) {
        toast.success(`${match.displayName} already has this number - selected them.`);
        onAdded(match);
        return;
      }
      const contact = await create.mutateAsync({
        displayName: name.trim(),
        phone: phone.trim(),
        source: 'QUOTATION',
      });
      toast.success(`${contact.displayName} added to Contacts.`);
      onAdded(contact);
    } catch (err) {
      const fields = fieldErrorsFromApiError(err);
      if (Object.keys(fields).length > 0) setErrors(fields);
      toast.error(err instanceof Error ? err.message : 'Could not add the contact.');
    } finally {
      setChecking(false);
    }
  }

  const busy = checking || create.isPending;
  return (
    <form
      onSubmit={(e) => void onSubmit(e)}
      noValidate
      style={{
        display: 'grid',
        gap: 'var(--space-2)',
        padding: 'var(--space-3)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      <strong style={{ fontSize: 'var(--font-size-sm)' }}>New customer</strong>
      <TextField
        label="Name *"
        placeholder="Customer name"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setErrors(({ displayName: _d, ...rest }) => rest);
        }}
        error={errors.displayName}
      />
      <TextField
        label="Mobile number *"
        placeholder="+91 98765 43210"
        inputMode="tel"
        value={phone}
        onChange={(e) => {
          setPhone(e.target.value);
          setErrors(({ phone: _p, ...rest }) => rest);
        }}
        error={errors.phone}
      />
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <Button type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Add & select'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
